import React from 'react';
import { Page, Text, View, StyleSheet } from '@react-pdf/renderer';

/**
 * Wallet-sized emergency cards for cutting out and carrying
 * 
 * Includes:
 * - Personal emergency info card
 * - Medical information card
 * - Family contact card
 */

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 9,
    backgroundColor: '#ffffff',
  },
  title: {
    fontSize: 20,
    fontFamily: 'Helvetica-Bold',
    color: '#1a1a1a',
    marginBottom: 10,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 20,
    textAlign: 'center',
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  card: {
    width: '48%',
    height: 180,
    border: '1.5pt dashed #9ca3af',
    borderRadius: 8,
    overflow: 'hidden',
  },
  cardFront: {
    padding: 10,
    backgroundColor: '#1f2937',
    height: '100%',
  },
  cardBack: {
    padding: 10,
    backgroundColor: '#f3f4f6',
    height: '100%',
  },
  cardTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#ffffff',
    marginBottom: 8,
    textAlign: 'center',
    backgroundColor: '#ef4444',
    padding: 4,
    marginHorizontal: -10,
    marginTop: -10,
  },
  cardTitleBack: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#1f2937',
    marginBottom: 6,
    textAlign: 'center',
    borderBottom: '1pt solid #d1d5db',
    paddingBottom: 4,
  },
  cardRow2: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  cardLabel: {
    fontSize: 7,
    color: '#d1d5db',
    width: 50,
  },
  cardValue: {
    fontSize: 8,
    color: '#ffffff',
    fontFamily: 'Helvetica-Bold',
    flex: 1,
  },
  cardItem: {
    fontSize: 7,
    color: '#374151',
    marginBottom: 2,
    paddingLeft: 8,
  },
  cardBullet: {
    fontSize: 7,
    color: '#ef4444',
    marginRight: 4,
  },
  instructions: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#fef3c7',
    borderRadius: 4,
  },
  instructionTitle: {
    fontSize: 10,
    fontFamily: 'Helvetica-Bold',
    color: '#92400e',
    marginBottom: 8,
  },
  instructionText: {
    fontSize: 8,
    color: '#78350f',
    marginBottom: 4,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 8,
    color: '#9ca3af',
  },
});

interface WalletCardsProps {
  data: {
    name: string;
    phone?: string;
    email?: string;
    address?: string;
    blood_type?: string;
    allergies?: string[];
    medications?: string[];
    conditions?: string[];
    emergency_contact?: {
      name: string;
      phone: string;
      relationship?: string;
    };
    family_contacts?: {
      name: string;
      phone: string;
      role?: string;
    }[];
    meeting_points?: {
      primary: string;
      secondary: string;
    };
  };
}

export const WalletCards: React.FC<WalletCardsProps> = ({ data }) => (
  <Page size="A4" style={styles.page}>
    <Text style={styles.title}>✂️ Emergency Wallet Cards</Text>
    <Text style={styles.subtitle}>
      Cut along dashed lines, fold in half, and keep in wallet or purse
    </Text>

    {/* Personal Emergency Card - Front & Back */}
    <View style={styles.cardRow}>
      {/* Front */}
      <View style={styles.card}>
        <View style={styles.cardFront}>
          <Text style={styles.cardTitle}>🚨 EMERGENCY INFO</Text>
          <View style={{ marginTop: 8 }}>
            <View style={styles.cardRow2}>
              <Text style={styles.cardLabel}>NAME:</Text>
              <Text style={styles.cardValue}>{data.name}</Text>
            </View>
            <View style={styles.cardRow2}>
              <Text style={styles.cardLabel}>PHONE:</Text>
              <Text style={styles.cardValue}>{data.phone || '___________'}</Text>
            </View>
            <View style={styles.cardRow2}>
              <Text style={styles.cardLabel}>ADDRESS:</Text>
              <Text style={styles.cardValue}>{data.address || '___________'}</Text>
            </View>
            <View style={{ marginTop: 8, borderTop: '0.5pt solid #4b5563', paddingTop: 6 }}>
              <Text style={{ ...styles.cardLabel, color: '#ef4444', marginBottom: 2 }}>
                ICE CONTACT:
              </Text>
              <Text style={styles.cardValue}>
                {data.emergency_contact?.name || '___________'}
              </Text>
              <Text style={styles.cardValue}>
                {data.emergency_contact?.phone || '___________'}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* Back */}
      <View style={styles.card}>
        <View style={styles.cardBack}>
          <Text style={styles.cardTitleBack}>EMERGENCY NUMBERS</Text>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>●</Text>
            <Text style={styles.cardItem}>Emergency: 911</Text>
          </View>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>●</Text>
            <Text style={styles.cardItem}>Poison Control: 1-800-222-1222</Text>
          </View>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>●</Text>
            <Text style={styles.cardItem}>Red Cross: 1-800-RED-CROSS</Text>
          </View>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>●</Text>
            <Text style={styles.cardItem}>FEMA: 1-800-621-3362</Text>
          </View>
          <View style={{ marginTop: 8 }}>
            <Text style={{ fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#374151', marginBottom: 2 }}>
              MEETING POINTS:
            </Text>
            <Text style={styles.cardItem}>
              1. {data.meeting_points?.primary || '___________'}
            </Text>
            <Text style={styles.cardItem}>
              2. {data.meeting_points?.secondary || '___________'}
            </Text>
          </View>
        </View>
      </View>
    </View>

    {/* Medical Info Card - Front & Back */}
    <View style={styles.cardRow}>
      {/* Front */}
      <View style={styles.card}>
        <View style={{ ...styles.cardFront, backgroundColor: '#7c3aed' }}>
          <Text style={{ ...styles.cardTitle, backgroundColor: '#dc2626' }}>
            ⚕️ MEDICAL INFO
          </Text>
          <View style={{ marginTop: 8 }}>
            <View style={styles.cardRow2}>
              <Text style={styles.cardLabel}>NAME:</Text>
              <Text style={styles.cardValue}>{data.name}</Text>
            </View>
            <View style={styles.cardRow2}>
              <Text style={styles.cardLabel}>BLOOD:</Text>
              <Text style={styles.cardValue}>{data.blood_type || '___'}</Text>
            </View>
            <View style={{ marginTop: 6 }}>
              <Text style={{ ...styles.cardLabel, marginBottom: 2 }}>ALLERGIES:</Text>
              {data.allergies?.length ? (
                data.allergies.slice(0, 3).map((allergy, idx) => (
                  <Text key={idx} style={{ ...styles.cardValue, fontSize: 7 }}>• {allergy}</Text>
                ))
              ) : (
                <Text style={{ ...styles.cardValue, fontSize: 7 }}>None listed</Text>
              )}
            </View>
          </View>
        </View>
      </View>

      {/* Back */}
      <View style={styles.card}>
        <View style={styles.cardBack}>
          <Text style={styles.cardTitleBack}>MEDICATIONS</Text>
          {data.medications?.length ? (
            data.medications.slice(0, 5).map((med, idx) => (
              <View key={idx} style={styles.cardRow2}>
                <Text style={styles.cardBullet}>●</Text>
                <Text style={styles.cardItem}>{med}</Text>
              </View>
            ))
          ) : (
            <Text style={styles.cardItem}>No medications listed</Text>
          )}
          <View style={{ marginTop: 8 }}>
            <Text style={{ fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#374151', marginBottom: 2 }}>
              CONDITIONS:
            </Text>
            {data.conditions?.length ? (
              data.conditions.slice(0, 3).map((condition, idx) => (
                <Text key={idx} style={styles.cardItem}>{condition}</Text>
              ))
            ) : (
              <Text style={styles.cardItem}>None listed</Text>
            )}
          </View>
        </View>
      </View>
    </View>

    {/* Family Contact Card - Front & Back */}
    <View style={styles.cardRow}>
      {/* Front */}
      <View style={styles.card}>
        <View style={{ ...styles.cardFront, backgroundColor: '#0369a1' }}>
          <Text style={{ ...styles.cardTitle, backgroundColor: '#0284c7' }}>
            👨‍👩‍👧‍👦 FAMILY CONTACTS
          </Text>
          <View style={{ marginTop: 6 }}>
            {data.family_contacts?.slice(0, 4).map((contact, idx) => (
              <View key={idx} style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 7, color: '#93c5fd' }}>
                  {contact.role || `Member ${idx + 1}`}:
                </Text>
                <Text style={{ ...styles.cardValue, fontSize: 7 }}>
                  {contact.name}: {contact.phone}
                </Text>
              </View>
            ))}
            {(!data.family_contacts || data.family_contacts.length === 0) && (
              <>
                <Text style={{ fontSize: 7, color: '#93c5fd' }}>Contact 1:</Text>
                <Text style={styles.cardValue}>___________</Text>
                <Text style={{ fontSize: 7, color: '#93c5fd', marginTop: 4 }}>Contact 2:</Text>
                <Text style={styles.cardValue}>___________</Text>
              </>
            )}
          </View>
        </View>
      </View>

      {/* Back */}
      <View style={styles.card}>
        <View style={styles.cardBack}>
          <Text style={styles.cardTitleBack}>QUICK ACTIONS</Text>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>1</Text>
            <Text style={styles.cardItem}>Stay calm, assess situation</Text>
          </View>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>2</Text>
            <Text style={styles.cardItem}>Call 911 if life-threatening</Text>
          </View>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>3</Text>
            <Text style={styles.cardItem}>Text family group: "I'm OK"</Text>
          </View>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>4</Text>
            <Text style={styles.cardItem}>Go to primary meeting point</Text>
          </View>
          <View style={styles.cardRow2}>
            <Text style={styles.cardBullet}>5</Text>
            <Text style={styles.cardItem}>Check in with out-of-area contact</Text>
          </View>
          <View style={{ marginTop: 6, backgroundColor: '#fef3c7', padding: 4, borderRadius: 2 }}>
            <Text style={{ fontSize: 6, color: '#92400e', textAlign: 'center' }}>
              If separated: Meet at secondary point in 2 hours
            </Text>
          </View>
        </View>
      </View>
    </View>

    {/* Instructions */}
    <View style={styles.instructions}>
      <Text style={styles.instructionTitle}>✂️ How to Use These Cards</Text>
      <Text style={styles.instructionText}>
        1. Cut along the dashed lines around each card
      </Text>
      <Text style={styles.instructionText}>
        2. Fold each card in half (front on outside, back on inside)
      </Text>
      <Text style={styles.instructionText}>
        3. Laminate if possible for durability
      </Text>
      <Text style={styles.instructionText}>
        4. Keep in wallet, car glove box, and emergency kit
      </Text>
      <Text style={styles.instructionText}>
        5. Give copies to all family members
      </Text>
    </View>

    <Text style={styles.footer}>
      Emergency Wallet Cards | Update annually or when information changes
    </Text>
  </Page>
);

export default WalletCards;
